import React, { useMemo } from 'react';
import clsx from 'clsx';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { LocalAudioTrack, LocalVideoTrack, Participant, RemoteAudioTrack, RemoteVideoTrack } from 'twilio-video';

import AudioLevelIndicator from '../AudioLevelIndicator/AudioLevelIndicator';
import AvatarIcon from '../../icons/AvatarIcon';
import NetworkQualityLevel from '../NetworkQualityLevel/NetworkQualityLevel';
import PinIcon from './PinIcon/PinIcon';
import ScreenShareIcon from '../../icons/ScreenShareIcon';
import Typography from '@material-ui/core/Typography';

import useIsTrackSwitchedOff from '../../hooks/useIsTrackSwitchedOff/useIsTrackSwitchedOff';
import usePublications from '../../hooks/usePublications/usePublications';
import useTrack from '../../hooks/useTrack/useTrack';
import useParticipantIsReconnecting from '../../hooks/useParticipantIsReconnecting/useParticipantIsReconnecting';
import MicIcon from '../../icons/MicIcon';
import MicOffIcon from '../../icons/MicOffIcon';
import VideoOnIcon from '../../icons/VideoOnIcon';
import VideoOffIcon from '../../icons/VideoOffIcon';
import useRoom from '../../hooks/useRoom/useRoom';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      // height: 0,
      overflow: 'hidden',
      width: '100%',
      height: '100%',
      // marginBottom: '2em',
      '& video': {
        filter: 'none',
        // objectFit: 'contain !important',
      },
      borderRadius: '4px',
      // border: `${theme.participantBorderWidth}px solid rgb(245, 248, 255)`,
      // paddingTop: `calc(${(9 / 16) * 100}% - ${theme.participantBorderWidth}px)`,
      background: 'black',
      [theme.breakpoints.down('sm')]: {
        // height: theme.sidebarMobileHeight,
        // width: `${(theme.sidebarMobileHeight * 16) / 9}px`,
        // marginRight: '8px',
        marginBottom: '0',
        fontSize: '10px',
        paddingTop: `${theme.sidebarMobileHeight - 2}px`,
      },
    },
    participantStatus: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      [theme.breakpoints.down(750)]: {
        width: '40%',
        // width:'50%',
      },
    },
    innerContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    infoContainer: {
      position: 'absolute',
      zIndex: 2,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%',
      width: '100%',
      background: 'transparent',
      top: 0,
    },
    avatarContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgb(38, 42, 44)',
      position: 'absolute',
      color: 'white',
      fontSize: '1.5rem',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      [theme.breakpoints.down('sm')]: {
        '& svg': {
          transform: 'scale(0.7)',
        },
      },
      [theme.breakpoints.down(300)]: {
        fontSize: '1rem',
      },
    },
    reconnectingContainer: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(40, 42, 43, 0.75)',
      zIndex: 1,
    },
    screenShareIconContainer: {
      background: 'rgba(0, 0, 0, 0.5)',
      padding: '0.18em 0.3em',
      marginRight: '0.3em',
      display: 'flex',
      '& path': {
        fill: 'white',
      },
    },
    identity: {
      background: 'rgba(0, 0, 0, 0.5)',
      color: 'white',
      padding: '0.18em 0.3em',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
    },
    infoRowBottom: {
      display: 'flex',
      justifyContent: 'space-between',
      position: 'absolute',
      bottom: 0,
      left: 0,
    },
    networkQualityContainer: {
      width: '28px',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.5)',
    },
    typeography: {
      color: 'white',
      [theme.breakpoints.down('sm')]: {
        fontSize: '0.75rem',
      },
    },
    hideParticipant: {
      display: 'none',
    },
    cursorPointer: {
      cursor: 'pointer',
    },
    video: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
    },
    audio: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
    },
    screen: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
    },
    participantIdentity: {
      color: '#919192',
      background: '#323132',
      fontSize: '18px',
      minWidth: '60px',
      textAlign: 'center',
      height: '35px',
      padding: '5px 5px',
      borderRadius: '10px',
      position: 'absolute',
      top: '2%',
      left: '2%',
      zIndex: 9999,
      [theme.breakpoints.down(750)]: {
        height: '25px',
        top: '3%',
        padding: ' 0px 5px',
      },
    },
  })
);

interface ParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
  isLocalParticipant?: boolean;
  hideParticipant?: boolean;
  showParticipantStatusOnly?: boolean;
}

export default function ParticipantInfo({
  participant,
  onClick,
  isSelected,
  children,
  isLocalParticipant,
  hideParticipant,
  showParticipantStatusOnly,
}: ParticipantInfoProps) {
  const publications = usePublications(participant);

  const audioPublication = publications.find(p => p.kind === 'audio');
  const videoPublication = publications.find(p => p.trackName.includes('camera'));

  const isVideoEnabled = Boolean(videoPublication);
  const isScreenShareEnabled = publications.find(p => p.trackName.includes('screen'));

  const videoTrack = useTrack(videoPublication);
  const isVideoSwitchedOff = useIsTrackSwitchedOff(videoTrack as LocalVideoTrack | RemoteVideoTrack);

  const audioTrack = useTrack(audioPublication) as LocalAudioTrack | RemoteAudioTrack | undefined;
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);

  const classes = useStyles();
  const { sessionState } = useRoom();
  const participantDetails = useMemo(
    () => sessionState.participantsDetails.find(par => par.userId === participant.identity),
    [sessionState.participantsDetails, participant]
  );

  return showParticipantStatusOnly ? (
    <div
      className={classes.participantStatus}
      // onClick={onClick}
      data-cy-participant={participant.identity}
    >
      <div className={classes.audio}>
        <AudioLevelIndicator audioTrack={audioTrack} />
      </div>
      <div className={classes.video}>{videoTrack ? <VideoOnIcon eVisit /> : <VideoOffIcon eVisit />}</div>

      <div className={classes.screen}>
        {isScreenShareEnabled ? <ScreenShareIcon active /> : <ScreenShareIcon active />}
      </div>
    </div>
  ) : (
    <div
      className={clsx(classes.container, {
        [classes.hideParticipant]: hideParticipant,
        [classes.cursorPointer]: Boolean(onClick),
      })}
      onClick={onClick}
      data-cy-participant={participant.identity}
    >
      {/* <div className={classes.infoContainer}>
         <div className={classes.networkQualityContainer}>
          <NetworkQualityLevel participant={participant} />
        </div> 
        <div className={classes.infoRowBottom}>
          {isScreenShareEnabled && (
            <span className={classes.screenShareIconContainer}>
              <ScreenShareIcon />
            </span>
          )}
          <span className={classes.identity}>
            <AudioLevelIndicator audioTrack={audioTrack} />
            <Typography variant="body1" className={classes.typeography} component="span">
              {participant.identity}
              {isLocalParticipant && ' (You)'}
            </Typography>
          </span>
        </div> 
          <div>{isSelected && <PinIcon />}</div>
        </div> */}
      <div className={classes.participantIdentity}>{participantDetails.userName}</div>
      <div className={classes.innerContainer}>
        {(!isVideoEnabled || isVideoSwitchedOff) && (
          <div className={classes.avatarContainer}>
            {/* <AvatarIcon /> */}
            {/* No camera selected */}
            This user has not selected a camera yet!
          </div>
        )}
        {isParticipantReconnecting && (
          <div className={classes.reconnectingContainer}>
            <Typography variant="body1" className={classes.typeography}>
              Reconnecting...
            </Typography>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
